import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Timer {
  duration?: number; // Total duration for countdown timers, optional for count-up timers
  elapsedTime?: number; // Elapsed time for count-up timers
  remainingTime?: number; // Remaining time for countdown timers
  isCountUp: boolean; // true for stopwatch mode
  paused: boolean; // Indicates if the timer is paused
}

interface TimerState {
  [id: string]: Timer;
}

const loadTimersFromStorage = (): TimerState => {
  const timers = localStorage.getItem('timers');
  return timers ? JSON.parse(timers) : {};
};

const saveTimersToStorage = (timers: TimerState): void => {
  localStorage.setItem('timers', JSON.stringify(timers));
};

const timerSlice = createSlice({
  name: 'timers',
  initialState: loadTimersFromStorage() as TimerState,
  reducers: {
    startTimer: (
      state,
      action: PayloadAction<{ id: string; duration?: number; isCountUp: boolean }>
    ) => {
      const { id, duration, isCountUp } = action.payload;

      state[id] = isCountUp
        ? { elapsedTime: 0, isCountUp, paused: false }
        : { duration, remainingTime: duration, isCountUp, paused: false };

      saveTimersToStorage(state);
    },
    updateTimer: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const timer = state[id];

      if (timer && !timer.paused) {
        if (timer.isCountUp) {
          // Increment elapsed time for count-up timers
          timer.elapsedTime = (timer.elapsedTime ?? 0) + 1;
        } else if (timer.remainingTime !== undefined) {
          // Decrement remaining time for countdown timers
          timer.remainingTime = Math.max(0, (timer.remainingTime ?? 0) - 1);
        }
        saveTimersToStorage(state);
      }
    },
    pauseTimer: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const timer = state[id];
      if (timer) {
        timer.paused = true;
        saveTimersToStorage(state);
      }
    },
    resumeTimer: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const timer = state[id];

      if (timer) {
        timer.paused = false;
        saveTimersToStorage(state);
      }
    },
    resetTimer: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      delete state[id];
      saveTimersToStorage(state);
    },
    setTimer: (
      state,
      action: PayloadAction<{ id: string; elapsedTime?: number; remainingTime?: number }>
    ) => {
      const { id, elapsedTime, remainingTime } = action.payload;
      const timer = state[id];

      if (timer) {
        if (timer.isCountUp && elapsedTime !== undefined) {
          timer.elapsedTime = elapsedTime;
        } else if (!timer.isCountUp && remainingTime !== undefined) {
          timer.remainingTime = remainingTime;
        }
        saveTimersToStorage(state);
      }
    },
  },
});

export const { startTimer, updateTimer, pauseTimer, resumeTimer, resetTimer, setTimer } = timerSlice.actions;
export default timerSlice.reducer;